import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowRight, ChevronDown } from 'lucide-react'
import { supportedLanguages, type SupportedLanguage } from '@/i18n'
import { translatePath } from '@/i18n/routes'
import { cn } from '@/lib/utils'

/**
 * Metadata for every language this selector knows how to render.
 * At runtime the dropdown is filtered down to the project's `supportedLanguages`,
 * so you can enable a new language simply by adding its code to
 * `src/i18n/index.ts` — as long as the metadata exists here the UI picks it up.
 */
interface LanguageMeta {
  code: string
  flag: string
  name: string
  short: string
}

const languagesMeta: LanguageMeta[] = [
  { code: 'it', flag: '🇮🇹', name: 'Italiano', short: 'IT' },
  { code: 'en', flag: '🇬🇧', name: 'English', short: 'EN' },
  { code: 'fr', flag: '🇫🇷', name: 'Français', short: 'FR' },
  { code: 'es', flag: '🇪🇸', name: 'Español', short: 'ES' },
  { code: 'de', flag: '🇩🇪', name: 'Deutsch', short: 'DE' },
  { code: 'pt', flag: '🇵🇹', name: 'Português', short: 'PT' },
  { code: 'pl', flag: '🇵🇱', name: 'Polski', short: 'PL' },
  { code: 'ja', flag: '🇯🇵', name: '日本語', short: 'JA' },
]

const languages = languagesMeta.filter(
  (l): l is LanguageMeta & { code: SupportedLanguage } =>
    (supportedLanguages as readonly string[]).includes(l.code)
)

/** Codice lingua ("IT", "EN") in Jost, leggermente spaziato. */
function CodeMark({ code, className }: { code: string; className?: string }) {
  return (
    <span aria-hidden="true" className={cn('font-body text-xs font-semibold tracking-[0.14em]', className)}>
      {code}
    </span>
  )
}

interface LanguageSelectorProps {
  variant?: 'desktop' | 'mobile'
}

export function LanguageSelector({ variant = 'desktop' }: LanguageSelectorProps) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const currentLang = (i18n.language || supportedLanguages[0]) as SupportedLanguage
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const currentLanguage =
    languages.find((l) => l.code === currentLang) || languages[0]

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setIsOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen])

  const switchLanguage = (newLang: SupportedLanguage) => {
    if (newLang === currentLang) {
      setIsOpen(false)
      return
    }

    const newPath = translatePath(location.pathname, newLang, location.search)

    i18n.changeLanguage(newLang)
    navigate(newPath)
    setIsOpen(false)
  }

  if (variant === 'mobile') {
    return (
      // Due colonne solo se ci stanno nome e codice interi (non nella sidebar dell'area riservata né a 320px).
      <div className="grid grid-cols-[repeat(auto-fit,minmax(8.5rem,1fr))] gap-3 py-3">
        {languages.map((lang) => {
          const isCurrent = lang.code === currentLang
          return (
            <button
              key={lang.code}
              type="button"
              onClick={() => switchLanguage(lang.code)}
              aria-current={isCurrent || undefined}
              className={cn(
                'flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left text-sm font-medium transition-colors',
                isCurrent ? 'border-sun bg-sun/15 text-current' : 'border-current/15 hover:border-current/40'
              )}
            >
              {lang.name}
              <CodeMark code={lang.short} className="opacity-70" />
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="group flex items-center gap-2 rounded-2xl px-2 py-1.5 text-sm font-medium transition-colors hover:bg-current/10"
        aria-expanded={isOpen}
        aria-controls="language-menu"
        aria-label={`${t('language.label')}: ${currentLanguage.name}`}
      >
        <CodeMark code={currentLanguage.short} />
        <ChevronDown aria-hidden="true" className={cn('size-3.5 transition-transform duration-300 ease-out-expo', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div
          id="language-menu"
          className={cn('absolute right-0 z-50 w-56 overflow-hidden', 'mt-3 origin-top-right', 'rounded-2xl border border-line bg-paper text-ink shadow-lift motion-safe:animate-reveal')}
        >
          <p className="inscription px-4 pt-3 pb-2 text-base text-sea-ink">{t('language.label')}</p>
          <span aria-hidden="true" className="ticket-perforation mx-4" />
          <ul className="p-1.5">
            {languages.map((lang) => {
              const isCurrent = lang.code === currentLang
              return (
                <li key={lang.code}>
                  <button
                    type="button"
                    onClick={() => switchLanguage(lang.code)}
                    aria-current={isCurrent || undefined}
                    className={cn(
                      'group/item flex w-full items-center gap-3 rounded-2xl px-2.5 py-2 text-left text-sm text-ink transition-colors hover:bg-paper-deep',
                      isCurrent && 'font-semibold'
                    )}
                  >
                    <CodeMark code={lang.short} className="w-6 text-ink-soft" />
                    <span className="flex-1">{lang.name}</span>
                    {isCurrent ? (
                      <span aria-hidden="true" className="size-2 rounded-full bg-sun" />
                    ) : (
                      <ArrowRight aria-hidden="true" className="size-3.5 -translate-x-1 text-ink-soft opacity-0 transition-[transform,opacity] duration-200 ease-out-expo group-hover/item:translate-x-0 group-hover/item:opacity-100" />
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
